<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, vue/html-closing-bracket-newline */
import { computed, nextTick, onMounted, reactive, ref } from 'vue';
import TalkviaReferralAPI from 'dashboard/api/talkviaReferral';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const data = ref({});
const isLoading = ref(false);
const isRegenerating = ref(false);
const regenerateDialogRef = ref(null);
const feedbackDialogRef = ref(null);
const feedback = reactive({
  title: '',
  description: '',
  type: 'edit',
});

const summary = computed(() => data.value.monthly_summary || {});
const currentLevel = computed(() => data.value.current_level || null);
const nextLevel = computed(() => data.value.next_level || null);
const referralsUntilNextLevel = computed(() => {
  if (!nextLevel.value) return 0;
  return Math.max(
    0,
    Number(nextLevel.value.minimum_active_referrals) -
      Number(data.value.active_referrals_count || 0)
  );
});
const levelProgress = computed(() => {
  if (!nextLevel.value) return 100;

  const activeCount = Number(data.value.active_referrals_count || 0);
  const currentMinimum = Number(
    currentLevel.value?.minimum_active_referrals || 0
  );
  const nextMinimum = Number(nextLevel.value.minimum_active_referrals || 0);
  const range = Math.max(1, nextMinimum - currentMinimum);

  return Math.min(
    100,
    Math.max(0, ((activeCount - currentMinimum) / range) * 100)
  );
});

const errorDescription = (error, fallback) =>
  error?.response?.data?.error || error?.response?.data?.message || fallback;

const showFeedback = async ({ title, description, type = 'edit' }) => {
  Object.assign(feedback, { title, description, type });
  await nextTick();
  feedbackDialogRef.value?.open();
};

const formatMoney = value =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(Number(value || 0));

const formatPercent = value =>
  `${new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(Number(value || 0))}%`;

const load = async () => {
  isLoading.value = true;
  try {
    const response = await TalkviaReferralAPI.get();
    data.value = response.data;
  } catch (error) {
    showFeedback({
      title: 'Não foi possível carregar',
      description: errorDescription(
        error,
        'Não foi possível carregar seu programa de indicações.'
      ),
      type: 'alert',
    });
  } finally {
    isLoading.value = false;
  }
};

const copyLink = async () => {
  try {
    await navigator.clipboard.writeText(data.value.referral_url);
    showFeedback({
      title: 'Link copiado',
      description: 'Seu link de indicação está pronto para ser compartilhado.',
    });
  } catch {
    showFeedback({
      title: 'Não foi possível copiar',
      description: 'Copie o endereço manualmente e tente novamente.',
      type: 'alert',
    });
  }
};

const openRegenerateDialog = () => regenerateDialogRef.value?.open();
const closeFeedback = () => feedbackDialogRef.value?.close();

const regenerateCode = async () => {
  isRegenerating.value = true;
  try {
    const response = await TalkviaReferralAPI.regenerateCode();
    data.value = { ...data.value, ...response.data };
    regenerateDialogRef.value?.close();
    showFeedback({
      title: 'Novo link criado',
      description:
        'O endereço anterior foi desativado. Você já pode compartilhar o novo link.',
    });
  } catch (error) {
    regenerateDialogRef.value?.close();
    showFeedback({
      title: 'Não foi possível gerar o link',
      description: errorDescription(
        error,
        'Não foi possível gerar outro link. Tente novamente.'
      ),
      type: 'alert',
    });
  } finally {
    isRegenerating.value = false;
  }
};

onMounted(load);
</script>

<template>
  <main class="h-full min-h-0 w-full overflow-y-auto">
    <div class="mx-auto flex w-full max-w-7xl flex-col gap-6 px-6 py-8 pb-24">
      <header
        class="relative isolate overflow-hidden rounded-2xl border border-n-weak bg-n-solid-2 p-6 sm:p-8"
      >
        <div
          class="pointer-events-none absolute -right-20 -top-24 size-72 rounded-full bg-n-brand/10 blur-3xl"
        />
        <div
          class="relative z-10 grid items-center gap-8 lg:grid-cols-[1fr_20rem]"
        >
          <div class="max-w-3xl">
            <h1
              class="mb-4 text-3xl font-semibold leading-tight text-n-slate-12 sm:text-4xl"
            >
              Indique. Cresça.
              <span class="text-n-brand">Ganhe mais.</span>
            </h1>
            <p
              class="mb-0 max-w-2xl text-base leading-7 text-n-slate-11 sm:text-lg"
            >
              Transforme cada indicação em benefícios reais para sua empresa.
              Quanto mais clientes ativos, maior seu retorno mensal e seu
              desconto no marketplace.
            </p>
          </div>

          <div
            class="rounded-xl border border-n-weak bg-n-solid-1 p-5 shadow-sm"
          >
            <div class="flex items-center justify-between gap-5">
              <div>
                <span
                  class="mb-1 block text-xs font-medium uppercase tracking-wide text-n-slate-10"
                  >Seu nível atual</span
                >
                <strong class="block text-2xl text-n-slate-12">
                  {{ currentLevel?.name || 'Inicial' }}
                </strong>
              </div>
              <div
                class="group relative grid size-14 shrink-0 place-items-center rounded-xl bg-n-amber-3 text-n-amber-11 ring-4 ring-n-amber-3/40"
              >
                <span
                  class="i-lucide-sparkles absolute -right-1 -top-1 size-4 text-n-amber-10 motion-safe:animate-pulse"
                />
                <span
                  class="i-lucide-trophy size-7 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:scale-110"
                />
              </div>
            </div>
            <div class="mt-5">
              <div class="mb-2 flex items-center justify-between gap-3 text-xs">
                <span class="text-n-slate-11">
                  {{ data.active_referrals_count || 0 }} clientes ativos
                </span>
                <span v-if="nextLevel" class="font-semibold text-n-slate-12">
                  Próximo: {{ nextLevel.name }}
                </span>
                <span v-else class="font-semibold text-n-slate-12">
                  Nível máximo
                </span>
              </div>
              <div class="h-2 overflow-hidden rounded-full bg-n-alpha-2">
                <div
                  class="h-full rounded-full bg-n-brand transition-all duration-700 ease-out"
                  :style="{ width: `${levelProgress}%` }"
                />
              </div>
            </div>
          </div>
        </div>
      </header>

      <div
        v-if="isLoading"
        class="rounded-xl border border-n-weak bg-n-solid-1 p-6"
      >
        Carregando seu programa de indicações...
      </div>

      <template v-else>
        <section
          class="rounded-2xl border border-n-weak bg-n-solid-1 p-5 shadow-sm sm:p-6"
        >
          <div class="mb-5 flex items-start gap-4">
            <div
              class="grid size-10 shrink-0 place-items-center rounded-xl bg-n-brand/10 text-n-brand"
            >
              <span class="i-lucide-link-2 size-5" />
            </div>
            <div>
              <h2 class="mb-1 text-lg font-semibold text-n-slate-12">
                Seu link exclusivo
              </h2>
              <p class="mb-0 text-sm text-n-slate-11">
                Toda conta criada por este endereço será vinculada
                automaticamente à sua empresa.
              </p>
            </div>
          </div>
          <div class="flex flex-col gap-3 md:flex-row">
            <input
              :value="data.referral_url"
              readonly
              class="h-11 min-w-0 flex-1 rounded-lg border border-n-weak bg-n-alpha-2 px-4 text-sm font-medium text-n-slate-12 outline-none transition focus:border-n-brand focus:ring-2 focus:ring-n-brand/20"
            />
            <Button
              label="Copiar link"
              icon="i-lucide-copy"
              :disabled="!data.referral_url"
              @click="copyLink"
            />
            <Button
              label="Gerar outro"
              icon="i-lucide-refresh-cw"
              color="slate"
              variant="outline"
              :disabled="isRegenerating"
              @click="openRegenerateDialog"
            />
          </div>
        </section>

        <section class="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <article
            class="group relative flex min-h-36 flex-col justify-end overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 p-5 transition hover:border-n-brand/30 hover:shadow-sm"
          >
            <span
              class="i-lucide-award absolute right-4 top-4 size-8 text-n-brand/30 transition group-hover:scale-110 group-hover:text-n-brand/60"
            />
            <span class="text-sm text-n-slate-11">Seu nível</span>
            <strong class="mt-4 text-2xl text-n-slate-12">{{
              currentLevel?.name || 'Inicial'
            }}</strong>
          </article>
          <article
            class="group relative flex min-h-36 flex-col justify-end overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 p-5 transition hover:border-n-brand/30 hover:shadow-sm"
          >
            <span
              class="i-lucide-users-round absolute right-4 top-4 size-8 text-n-brand/30 transition group-hover:scale-110 group-hover:text-n-brand/60"
            />
            <span class="text-sm text-n-slate-11">Clientes ativos</span>
            <strong class="mt-4 text-2xl text-n-slate-12">{{
              data.active_referrals_count || 0
            }}</strong>
          </article>
          <article
            class="group relative flex min-h-36 flex-col justify-end overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 p-5 transition hover:border-n-brand/30 hover:shadow-sm"
          >
            <span
              class="i-lucide-hand-coins absolute right-4 top-4 size-8 text-n-brand/30 transition group-hover:scale-110 group-hover:text-n-brand/60"
            />
            <span class="text-sm text-n-slate-11"
              >Benefício sobre mensalidades</span
            >
            <strong class="mt-4 text-2xl text-n-slate-12">{{
              formatPercent(summary.benefit_percentage)
            }}</strong>
          </article>
          <article
            class="group relative flex min-h-36 flex-col justify-end overflow-hidden rounded-2xl border border-n-weak bg-n-solid-1 p-5 transition hover:border-n-brand/30 hover:shadow-sm"
          >
            <span
              class="i-lucide-shopping-bag absolute right-4 top-4 size-8 text-n-brand/30 transition group-hover:scale-110 group-hover:text-n-brand/60"
            />
            <span class="text-sm text-n-slate-11">Desconto no marketplace</span>
            <strong class="mt-4 text-2xl text-n-slate-12">{{
              formatPercent(data.marketplace_discount_percentage)
            }}</strong>
          </article>
        </section>

        <section
          v-if="nextLevel"
          class="rounded-xl border border-n-brand/30 bg-n-brand/5 p-5"
        >
          <strong class="text-n-slate-12">
            Faltam {{ referralsUntilNextLevel }} cliente(s) ativo(s) para o
            nível {{ nextLevel.name }}.
          </strong>
          <p class="mb-0 mt-1 text-sm text-n-slate-11">
            Nesse nível:
            {{ formatPercent(nextLevel.monthly_benefit_percentage) }} sobre
            mensalidades e
            {{ formatPercent(nextLevel.marketplace_discount_percentage) }} no
            marketplace.
          </p>
        </section>

        <section class="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
          <article class="rounded-xl border border-n-weak bg-n-solid-1 p-6">
            <div class="mb-5 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 class="mb-1 text-lg font-semibold text-n-slate-12">
                  Benefício do mês
                </h2>
                <p class="mb-0 text-sm text-n-slate-11">
                  Calculado sobre as mensalidades pagas pelos clientes
                  indicados.
                </p>
              </div>
              <strong class="text-2xl text-n-brand">{{
                formatMoney(summary.benefit_total)
              }}</strong>
            </div>
            <div
              v-if="!summary.clients?.length"
              class="rounded-lg bg-n-alpha-2 p-4 text-sm text-n-slate-11"
            >
              Seus clientes indicados aparecerão aqui após o cadastro.
            </div>
            <div v-else class="overflow-x-auto">
              <table class="min-w-full divide-y divide-n-weak text-sm">
                <thead>
                  <tr class="text-left text-xs uppercase text-n-slate-10">
                    <th class="py-3">Cliente</th>
                    <th class="py-3">Status</th>
                    <th class="py-3 text-right">Pago no mês</th>
                    <th class="py-3 text-right">Benefício</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-n-weak">
                  <tr v-for="client in summary.clients" :key="client.id">
                    <td class="py-3 font-medium text-n-slate-12">
                      {{ client.name }}
                    </td>
                    <td class="py-3 text-n-slate-11">
                      {{ client.active ? 'Ativo' : 'Suspenso' }}
                    </td>
                    <td class="py-3 text-right text-n-slate-12">
                      {{ formatMoney(client.paid_total) }}
                    </td>
                    <td class="py-3 text-right font-semibold text-n-slate-12">
                      {{ formatMoney(client.benefit) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </article>

          <article class="rounded-xl border border-n-weak bg-n-solid-1 p-6">
            <h2 class="mb-1 text-lg font-semibold text-n-slate-12">
              Níveis disponíveis
            </h2>
            <p class="mb-5 text-sm text-n-slate-11">
              Os benefícios evoluem automaticamente.
            </p>
            <ol class="m-0 grid list-none gap-3 p-0">
              <li
                v-for="level in data.levels"
                :key="level.id"
                class="rounded-lg border p-4"
                :class="
                  currentLevel?.id === level.id
                    ? 'border-n-brand bg-n-brand/5'
                    : 'border-n-weak bg-n-alpha-1'
                "
              >
                <div class="flex items-center justify-between gap-3">
                  <strong class="text-n-slate-12">{{ level.name }}</strong>
                  <span class="text-xs font-semibold text-n-slate-10">
                    {{ level.minimum_active_referrals }}+ ativos
                  </span>
                </div>
                <p
                  v-if="level.description"
                  class="mb-2 mt-1 text-sm text-n-slate-11"
                >
                  {{ level.description }}
                </p>
                <p class="mb-0 text-sm text-n-slate-11">
                  {{ formatPercent(level.monthly_benefit_percentage) }}
                  mensalidades ·
                  {{ formatPercent(level.marketplace_discount_percentage) }}
                  marketplace
                </p>
              </li>
            </ol>
          </article>
        </section>
      </template>
    </div>

    <Dialog
      ref="regenerateDialogRef"
      type="alert"
      width="md"
      title="Gerar um novo link?"
      description="O endereço atual será desativado imediatamente e não poderá mais ser usado em novos cadastros."
      cancel-button-label="Manter link atual"
      confirm-button-label="Gerar novo link"
      :is-loading="isRegenerating"
      @confirm="regenerateCode"
    >
      <div class="rounded-lg border border-n-ruby-6 bg-n-ruby-3 p-4">
        <div class="flex items-start gap-3">
          <span
            class="i-lucide-triangle-alert mt-0.5 size-5 shrink-0 text-n-ruby-10"
          />
          <p class="mb-0 text-sm leading-6 text-n-ruby-11">
            Atualize o link em campanhas, mensagens e materiais onde ele já foi
            compartilhado.
          </p>
        </div>
      </div>
    </Dialog>

    <Dialog
      ref="feedbackDialogRef"
      :type="feedback.type"
      width="sm"
      :title="feedback.title"
      :description="feedback.description"
      :show-cancel-button="false"
      confirm-button-label="Entendi"
      @confirm="closeFeedback"
    />
  </main>
</template>
